import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import TicketTableDashboard from './TicketTableDashboard';
import { useTranslation } from 'react-i18next';
import { FaTicketAlt, FaRobot, FaExclamationCircle } from 'react-icons/fa';
import './Tickets.css';

const Tickets = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [tickets, setTickets] = useState([]);
    const [assignedTickets, setAssignedTickets] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [assignedCurrentPage, setAssignedCurrentPage] = useState(1);
    const [paginationInfo, setPaginationInfo] = useState(null);
    const [assignedPaginationInfo, setAssignedPaginationInfo] = useState(null);
    const [ticketStats, setTicketStats] = useState({
        remaining_tickets: 0,
        automated_tickets: 0,
        pending_tickets: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [brands, setBrands] = useState([]);
    const [selectedBrand, setSelectedBrand] = useState(null);
    const [currentFilters, setCurrentFilters] = useState({
        topics: [],
        separate_assigned: true
    });
    const [currentViewMode, setCurrentViewMode] = useState('unassigned');

    useEffect(() => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            navigate('/');
            return;
        }

        const fetchAllData = async () => {
            try {
                setLoading(true);
                
                // Fetch user data first
                const userData = await fetchUserData(token);
                
                // Set initial filters based on user type (same as Dashboard.js)
                const initialFilters = {
                    topics: [],
                    separate_assigned: true
                };
                setCurrentFilters(initialFilters);
                
                // Fetch tickets and stats
                await Promise.all([
                    fetchTickets(token, 1, initialFilters, 1),
                    fetchTicketStats(token)
                ]);
                
                // Fetch brands if user has brands
                if (userData?.has_brand) {
                    await fetchBrands(token);
                }
                
            } catch (error) {
                console.error('Failed to fetch data:', error);
                setError(error.message || 'An error occurred while fetching data');
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();
    }, [navigate]);

    const fetchUserData = async (token) => {
        try {
            const response = await axios.get('/api/users/user', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUser(response.data);
            return response.data;
        } catch (error) {
            console.error('Failed to fetch user data:', error);
            setUser(null);
            return null;
        }
    };

    const fetchTickets = async (token, page = 1, filters = null, assignedPage = 1) => {
        try {
            const ticketsPerPage = 25;
            const activeFilters = filters || currentFilters;
            
            // Build query parameters (same as Dashboard.js)
            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: ticketsPerPage.toString(),
            });

            if (activeFilters.separate_assigned) {
                queryParams.append('assigned_page', assignedPage.toString());
                queryParams.append('assigned_limit', ticketsPerPage.toString());
                queryParams.append('separate_assigned', 'true');
            }

            if (activeFilters.topics && activeFilters.topics.length > 0) {
                activeFilters.topics.forEach(topic => {
                    queryParams.append('topics', topic);
                });
            }

            if (activeFilters.viewId) {
                queryParams.append('view_id', activeFilters.viewId);
            }

            const response = await axios.get(`/api/tickets/tickets?${queryParams}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data) {
                if (activeFilters.separate_assigned) {
                    // Handle separated response
                    setTickets(response.data.unassigned_tickets || []);
                    setAssignedTickets(response.data.assigned_tickets || []);
                    setPaginationInfo(response.data.unassigned_pagination);
                    setAssignedPaginationInfo(response.data.assigned_pagination);
                } else {
                    // Handle simple response
                    setTickets(response.data.tickets || []);
                    setAssignedTickets([]);
                    setPaginationInfo(response.data.pagination);
                    setAssignedPaginationInfo(null);
                }
                
                setCurrentPage(page);
                setAssignedCurrentPage(assignedPage);
            } else {
                setTickets([]);
                setAssignedTickets([]);
                setPaginationInfo(null);
                setAssignedPaginationInfo(null);
            }
        } catch (error) {
            console.error('Failed to fetch tickets:', error);
            setTickets([]);
            setAssignedTickets([]);
            setPaginationInfo(null);
            setAssignedPaginationInfo(null);
        }
    };

    const fetchTicketStats = async (token) => {
        try {
            const response = await axios.get('/api/tickets/stats', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTicketStats(response.data);
        } catch (error) {
            console.error('Failed to fetch ticket stats:', error);
        }
    };

    const fetchBrands = async (token) => {
        try {
            const response = await axios.get('/api/brands/list', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBrands(response.data || []);
            if (response.data && response.data.length > 0) {
                setSelectedBrand(response.data[0].id);
            }
        } catch (error) {
            console.error('Failed to fetch brands:', error);
            setBrands([]);
        }
    };

    const handleTicketClick = (ticketId) => {
        navigate(`/tickets/${ticketId}/messages`);
    };

    const handleFilterChange = (newFilters) => {
        const updatedFilters = { ...currentFilters, ...newFilters };
        setCurrentFilters(updatedFilters);
        
        if (newFilters.viewMode !== undefined) {
            setCurrentViewMode(newFilters.viewMode);
        }
        
        const token = localStorage.getItem('access_token');
        if (token && user?.role !== 'experience') {
            fetchTickets(token, 1, updatedFilters, 1);
        }
    };

    const handlePageChange = (newUnassignedPage, newAssignedPage) => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            return;
        }

        const hasUnassignedChange = newUnassignedPage !== null && newUnassignedPage !== undefined;
        const hasAssignedChange = newAssignedPage !== null && newAssignedPage !== undefined;

        if (!hasUnassignedChange && !hasAssignedChange) {
            return;
        }

        const targetUnassigned = hasUnassignedChange ? newUnassignedPage : currentPage;
        const targetAssigned = hasAssignedChange ? newAssignedPage : assignedCurrentPage;

        if (currentFilters.viewId) {
            fetchTickets(token, targetUnassigned, currentFilters, targetAssigned);
            return;
        }

        if (currentViewMode === 'assigned') {
            if (hasAssignedChange && newAssignedPage > 0 && (!assignedPaginationInfo?.total_pages || newAssignedPage <= assignedPaginationInfo.total_pages)) {
                fetchTickets(token, currentPage, currentFilters, newAssignedPage);
            }
            return;
        }

        if (hasUnassignedChange && newUnassignedPage > 0 && (!paginationInfo?.total_pages || newUnassignedPage <= paginationInfo.total_pages)) {
            fetchTickets(token, newUnassignedPage, currentFilters, assignedCurrentPage);
        }
    };

    const handleBrandChange = (brandId) => {
        setSelectedBrand(brandId);
    };

    if (loading) return <div className="una-loading">Loading...</div>;
    if (error) return <div className="una-error">{error}</div>;

    return (
        <div className="una-tickets-container">
            {/* Stats Cards */}
            <div className="una-tickets-stats">
                <div className="una-stat-card">
                    <div className="una-stat-icon">
                        <FaTicketAlt />
                    </div>
                    <div className="una-stat-content">
                        <span className="una-stat-value">{ticketStats.remaining_tickets}</span>
                        <span className="una-stat-label">{t('remainingTickets')}</span>
                    </div>
                </div>

                <div className="una-stat-card">
                    <div className="una-stat-icon automated">
                        <FaRobot />
                    </div>
                    <div className="una-stat-content">
                        <span className="una-stat-value">{ticketStats.automated_tickets}</span>
                        <span className="una-stat-label">{t('automatedTickets')}</span>
                    </div>
                </div>

                <div className="una-stat-card">
                    <div className="una-stat-icon pending">
                        <FaExclamationCircle />
                    </div>
                    <div className="una-stat-content">
                        <span className="una-stat-value">{ticketStats.pending_tickets}</span>
                        <span className="una-stat-label">{t('pendingTickets')}</span>
                    </div>
                </div>
            </div>

            {/* Tickets Table */}
            <div className="una-tickets-table">
                <TicketTableDashboard 
                    tickets={tickets}
                    assignedTickets={assignedTickets}
                    user={user}
                    onTicketClick={handleTicketClick}
                    hasFaAccount={user?.fa_account}
                    brands={brands}
                    selectedBrand={selectedBrand}
                    onBrandChange={handleBrandChange}
                    pagination={paginationInfo}
                    assignedPagination={assignedPaginationInfo}
                    onPageChange={handlePageChange}
                    onFilterChange={handleFilterChange}
                    currentViewMode={currentViewMode}
                />
            </div>
        </div>
    );
};

export default Tickets;